import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { api, formatApiError } from '@/lib/api';

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/auth/invite/${token}`)
      .then((res) => setInvite(res.data))
      .catch((err) => setInviteError(formatApiError(err.response?.data?.detail) || 'This invitation is not valid.'));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true);
    try {
      await acceptInvite(token, name, password);
      navigate('/portal', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Could not create account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />
      <div className="pt-32 md:pt-40 pb-24 container mx-auto">
        <div className="max-w-md mx-auto rounded-3xl bg-card border border-border/70 shadow-card p-8 sm:p-10">
          <div className="h-12 w-12 rounded-2xl gradient-honey grid place-items-center text-accent-foreground shadow-amber">
            <Sparkles className="h-5 w-5" />
          </div>
          {inviteError ? (
            <div data-testid="invite-error">
              <h1 className="font-display text-3xl font-semibold text-primary mt-5">Invitation issue</h1>
              <p className="text-sm text-muted-foreground mt-2">{inviteError}</p>
              <p className="text-sm text-muted-foreground mt-2">
                Need a new invitation? Email <a className="text-leaf hover:underline underline-offset-4" href="mailto:toronto@vegandanishbakery.com">toronto@vegandanishbakery.com</a>.
              </p>
            </div>
          ) : !invite ? (
            <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-leaf" /></div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold text-primary mt-5">Welcome aboard.</h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                Set up your ordering account for <strong className="text-primary">{invite.org}</strong> ({invite.email}).
              </p>
              <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="invite-form">
                <div>
                  <Label htmlFor="inv-name" className="text-primary mb-1.5">Your name</Label>
                  <Input id="inv-name" required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Maren Holt" className="h-11 rounded-xl" data-testid="invite-name-input" />
                </div>
                <div>
                  <Label htmlFor="inv-pass" className="text-primary mb-1.5">Password (min. 8 characters)</Label>
                  <Input id="inv-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-xl" data-testid="invite-password-input" />
                </div>
                <div>
                  <Label htmlFor="inv-confirm" className="text-primary mb-1.5">Confirm password</Label>
                  <Input id="inv-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="h-11 rounded-xl" data-testid="invite-confirm-input" />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="invite-form-error">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="invite-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account & sign in'}
                </Button>
              </form>
            </>
          )}
          <p className="text-xs text-muted-foreground mt-6 text-center">
            Already have an account? <Link to="/login" className="text-leaf hover:underline underline-offset-4">Sign in</Link>
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
